import { Question } from './types';

export type GridSpan = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

const DEFAULT_GRID_SPAN: GridSpan = 3;

export const SPAN_CLASSES: Record<GridSpan, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12',
};

/**
 * Returns the Tailwind col-span class for a child item relative to its parent container.
 * Exported for reuse across components that render question sub-grids.
 */
export const getSubgroupChildSpanClass = (
  childSpan: GridSpan,
  parentSpan: GridSpan
): string => {
  const cappedChild = Math.min(childSpan, parentSpan);
  const cols = Math.max(
    1,
    Math.min(12, Math.round((cappedChild * 12) / parentSpan))
  ) as GridSpan;
  return SPAN_CLASSES[cols];
};

/**
 * Heuristic fallback column span when no authoritative width is set.
 * Exported for testing.
 */
export const getQuestionSpan = (question: Question): GridSpan => {
  if (question.type === 'multiselect') {
    if (question.values && question.values.length > 8) return 12;
    if (question.values && question.values.length > 4) return 6;
    return DEFAULT_GRID_SPAN;
  }

  if (question.type === 'text') {
    const q = question.question.toLowerCase();
    if (
      q.includes('describe') ||
      q.includes('explain') ||
      q.includes('details') ||
      q.includes('comments')
    ) {
      return 12;
    }
    return DEFAULT_GRID_SPAN;
  }

  if (question.type === 'radio' && question.values) {
    if (question.values.length > 6) return 12;
    if (question.values.length > 3) return 6;
    return DEFAULT_GRID_SPAN;
  }

  return DEFAULT_GRID_SPAN;
};

/**
 * Resolves the effective grid span for a question using the following precedence:
 *  1. Active arrange-mode override for this qid
 *  2. roger_emr_display_width from the question schema (authoritative)
 *  3. Heuristic based on question type/values
 * Exported for testing.
 */
export const resolveQuestionSpan = (
  question: Question,
  spanOverrides?: Record<string, GridSpan>
): GridSpan => {
  if (spanOverrides?.[question.qid] !== undefined) {
    return spanOverrides[question.qid];
  }
  if (
    question.roger_emr_display_width !== undefined &&
    question.roger_emr_display_width >= 1 &&
    question.roger_emr_display_width <= 12
  ) {
    return question.roger_emr_display_width as GridSpan;
  }
  return getQuestionSpan(question);
};

const getDirectSubitems = (question: Question): Question[] =>
  Object.values(question.subitems ?? {}).flatMap(items => items);

export const reorderList = <T>(
  items: T[],
  fromIdx: number,
  toIdx: number
): T[] => {
  if (
    fromIdx === toIdx ||
    fromIdx < 0 ||
    toIdx < 0 ||
    fromIdx >= items.length ||
    toIdx >= items.length
  ) {
    return items;
  }

  const reordered = [...items];
  const [moved] = reordered.splice(fromIdx, 1);
  reordered.splice(toIdx, 0, moved);
  return reordered;
};

const findQuestionByQid = (
  questions: Question[],
  targetQid: string
): Question | null => {
  for (const question of questions) {
    if (question.qid === targetQid) {
      return question;
    }

    const nestedQuestion = findQuestionByQid(
      getDirectSubitems(question),
      targetQid
    );
    if (nestedQuestion) {
      return nestedQuestion;
    }
  }

  return null;
};

export const collectSubitemOrderByParentQid = (
  questions: Question[]
): Record<string, string[]> => {
  const orderByParentQid: Record<string, string[]> = {};

  const visit = (question: Question) => {
    const directSubitems = getDirectSubitems(question);
    if (directSubitems.length > 0) {
      orderByParentQid[question.qid] = directSubitems.map(
        subitem => subitem.qid
      );
      directSubitems.forEach(visit);
    }
  };

  questions.forEach(visit);
  return orderByParentQid;
};

export const reorderNestedSubitemsInQuestions = (
  questions: Question[],
  parentQid: string,
  fromIdx: number,
  toIdx: number
): Question[] => {
  const targetQuestion = findQuestionByQid(questions, parentQid);
  if (!targetQuestion) {
    return questions;
  }

  const currentChildOrder = getDirectSubitems(targetQuestion).map(
    child => child.qid
  );
  const nextChildOrder = reorderList(currentChildOrder, fromIdx, toIdx);
  if (nextChildOrder === currentChildOrder) {
    return questions;
  }

  const childOrderIndex = new Map(
    nextChildOrder.map((qid, index) => [qid, index] as const)
  );

  const updateQuestion = (question: Question): [Question, boolean] => {
    if (!question.subitems) {
      return [question, false];
    }

    let changed = false;
    const updatedSubitemsEntries = Object.entries(question.subitems).map(
      ([branch, branchItems]) => {
        const updatedChildren = branchItems.map(child => {
          const [updatedChild, childChanged] = updateQuestion(child);
          if (childChanged) {
            changed = true;
          }
          return updatedChild;
        });

        const reorderedChildren =
          question.qid === parentQid
            ? [...updatedChildren].sort(
                (left, right) =>
                  (childOrderIndex.get(left.qid) ?? Number.MAX_SAFE_INTEGER) -
                  (childOrderIndex.get(right.qid) ?? Number.MAX_SAFE_INTEGER)
              )
            : updatedChildren;

        if (
          reorderedChildren.some(
            (child, index) => child.qid !== updatedChildren[index]?.qid
          )
        ) {
          changed = true;
        }

        return [branch, reorderedChildren] as const;
      }
    );

    if (!changed) {
      return [question, false];
    }

    return [
      {
        ...question,
        subitems: Object.fromEntries(updatedSubitemsEntries),
      },
      true,
    ];
  };

  let anyChanged = false;
  const updatedQuestions = questions.map(question => {
    const [updatedQuestion, questionChanged] = updateQuestion(question);
    if (questionChanged) {
      anyChanged = true;
    }
    return updatedQuestion;
  });

  return anyChanged ? updatedQuestions : questions;
};
